// src/hooks/useDashboardActions.js
import { useCallback } from "react";
import { useDashboard } from "../contexts/DashboardContext";
import { useAuth } from "../contexts/AuthContext";
import useDashboardApi from "../utils/dashboardApi"; // Keep this for specific API calls not handled by context
import { getTierSlotCount } from "../utils/constants";

export const useDashboardActions = () => {
  const { userObjectId } = useAuth();
  const { refreshDashboard, setIsDeleting, fetchDashboardData } =
    useDashboard();
  const { addDivesiteToDashboard, updateDashboardSlots, getDashboard } =
    useDashboardApi();

  const handleAddDivesite = useCallback(
    async (divesite) => {
      try {
        const currentDashboard = await getDashboard(userObjectId);
        if (!currentDashboard) {
          console.error("handleAddDivesite: Invalid dashboard data received");
          return;
        }

        const userTier = currentDashboard.tier || 1;
        const maxSlots = getTierSlotCount(userTier);
        const currentSlots = currentDashboard.slots || [];
        const slotIds = currentSlots
          .map((slot) => (typeof slot === "object" ? slot._id : slot))
          .filter((id) => id !== null && id !== undefined);

        if (slotIds.length >= maxSlots) {
          console.warn("handleAddDivesite: Dashboard is already full.");
          // Refresh dashboard data to update available slots if needed
          refreshDashboard();
          return;
        }

        const updatedSlots = [...slotIds, divesite._id];

        if (updatedSlots.length > maxSlots) {
          console.warn(
            `handleAddDivesite: Can't exceed max slots (${maxSlots})`
          );
          return;
        }

        await addDivesiteToDashboard(userObjectId, updatedSlots);
        refreshDashboard();
      } catch (err) {
        console.error("Error adding divesite to dashboard:", err);
      }
    },
    [userObjectId, addDivesiteToDashboard, getDashboard, refreshDashboard]
  );

  const handleRemoveDivesite = useCallback(
    async (divesiteId) => {
      setIsDeleting(true);
      try {
        const currentDashboard = await fetchDashboardData(userObjectId); // Fetch current data
        if (!currentDashboard) {
          console.error(
            "handleRemoveDivesite: Invalid dashboard data received"
          );
          return;
        }

        const currentSlots = currentDashboard.slots || [];
        const slotIds = currentSlots
          .map((slot) => (typeof slot === "object" ? slot._id : slot))
          .filter((id) => id !== null && id !== undefined);

        const updatedSlots = slotIds.filter((id) => id !== divesiteId);

        await updateDashboardSlots(userObjectId, updatedSlots);
        refreshDashboard();
      } catch (err) {
        console.error("Error removing divesite from dashboard:", err);
      } finally {
        setIsDeleting(false);
      }
    },
    [
      userObjectId,
      updateDashboardSlots,
      refreshDashboard,
      setIsDeleting,
      fetchDashboardData,
    ]
  );

  return {
    handleAddDivesite,
    handleRemoveDivesite,
  };
};
